import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastVariants } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import {
  setAllDrawersClosed,
  setIsClearConfirmationDialogClosed,
  setIsDeleteConfirmationDialogClosed,
  setIsDeleteConfirmationDialogOpen,
  setIsImageEditDrawerOpen,
  setIsResetConfirmationDialogClosed,
  setIsTextDrawerOpen,
  setIsToasterOpen,
  useAppContext,
} from '../../../../context/app-context';
import { useCardContext } from '../../../../context/card-context';
import { useCropContext } from '../../../../context/crop-context';
import { useInitializationDataContext } from '../../../../context/data-context';
import { usePhotoControls } from '../../../../features/card-editor/hooks/use-photo-controls';
import { isFabricImage } from '../../../../features/card-editor/utils/is-fabric-image';
import { isPhotozoneImage } from '../../../../features/card-editor/utils/is-photozone-image';
import { FabricObject, FabricTextBox } from '../../../../global-types';
import { useFeatureFlags } from '../../../../hooks';
import { ActionType, useDatadog } from '../../../../hooks/use-datadog';
import { useIsOneToMany } from '../../../../hooks/use-is-one-to-many';
import { deleteImage } from '../../../../services';
import {
  CanvasDataTypes,
  clearPhotoTextZone,
  convertPointToPixel,
  getObjectByName,
  togglePhotoTextZoneButtons,
} from '../../../../utils';
import { parseTextAction } from '../../../../utils/parse-text-action';
import { addEditableAreas } from '../../../card-editor/utils';
import { handleOrderChange } from '../utils/handle-order-change';
import { isUserZoneEmpty } from '../utils/is-user-zone-empty';

export const useDrawerContainerActions = (
  canvas: React.RefObject<fabric.Canvas> | undefined,
  setInitialAngle: React.Dispatch<React.SetStateAction<number>>,
) => {
  const { addAction, textAction } = useDatadog();
  const { appDispatch } = useAppContext();
  const { finishCropping } = useCropContext();
  const { updateEditFormats, trackEditElement, trackDeleteElement, isReplacingImage, trackWamTamReset } =
    useAnalyticsContext();
  const { SAS_DYNAMIC_BUTTONS } = useFeatureFlags();
  const { cardState } = useCardContext();
  const isOneToMany = useIsOneToMany();
  const { t } = useTranslation();
  const { deleteSelectedPhoto, changePhotoOrder } = usePhotoControls();

  const openImageEditDrawer = useCallback(() => setIsImageEditDrawerOpen(appDispatch), [appDispatch]);

  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  const resetErrorMessage = t('drawerContainer.resetErrorMessage');
  const imageErrorTitle = t('drawerContainer.imageErrorTitle');

  const handleRotateOpenDrawer = () => {
    const activeObject = canvas?.current?.getActiveObject();
    if (activeObject?.type === 'image') {
      openImageEditDrawer();
    } else {
      setIsTextDrawerOpen(appDispatch);
    }
  };

  // TO DO Check if we use this function in other fabric objects besides images
  const onChangeOrder = (order: number, object?: fabric.Object) => {
    if (isFabricImage(object)) {
      changePhotoOrder(order, object);
      return;
    }
    if (!canvas?.current) return;
    handleOrderChange(order, canvas.current, object).then(() => {
      updateEditFormats({ order: order.toString() });
    });
  };

  const openDeleteConfirmationDialog = () => {
    setIsDeleteConfirmationDialogOpen(appDispatch);
  };

  const closeDeleteConfirmationDialog = () => {
    setIsDeleteConfirmationDialogClosed(appDispatch);
  };

  const closeClearConfirmationDialog = () => {
    trackDeleteElement();
    setIsClearConfirmationDialogClosed(appDispatch);
    resetSelection();
  };

  const onSubmitSize = () => {
    setIsTextDrawerOpen(appDispatch);
  };

  const onSubmitRotate = () => {
    const activeObject = canvas?.current?.getActiveObject();
    setInitialAngle(activeObject?.angle ?? 0);

    if (activeObject && isPhotozoneImage(activeObject)) {
      // Reset scaleValue to 100 when applying rotation for photozone images since the scale is recalculated
      // to ensure the image still covers the photozone after rotation
      activeObject?.set({
        data: { ...activeObject.data },
      });
    }

    handleRotateOpenDrawer();
  };

  const onSubmitOrder = () => {
    const activeObject = canvas?.current?.getActiveObject();

    if (activeObject?.type === 'image') {
      setIsImageEditDrawerOpen(appDispatch);
    } else {
      setIsTextDrawerOpen(appDispatch);
    }
  };

  const onFinishCropping = async () => {
    if (finishCropping) {
      await finishCropping();
    }
  };

  const onCloseImageEditDrawer = () => {
    if (!isReplacingImage.current) {
      trackEditElement();
      resetSelection();
    }
  };

  /**
   *  When size drawer is closed by cancel, revert text to its original size
   *
   * @param textObject - object to revert back to its original size
   * @param fontSize - the text object's original size

   * @returns void
   */
  const onSizeDrawerClose = (textObject: fabric.Textbox, fontSize: number) => {
    setIsTextDrawerOpen(appDispatch);
    textObject?.setOptions({ fontSize });
    canvas?.current?.renderAll();
  };

  const closeResetConfirmationDialog = () => {
    setIsResetConfirmationDialogClosed(appDispatch);
  };

  const resetSelection = () => {
    setAllDrawersClosed(appDispatch);
    if (canvas?.current) {
      canvas.current.discardActiveObject();
      canvas.current.renderAll();
    }
  };

  const deleteSelectedObject = () => {
    const activeObject = canvas?.current?.getActiveObject();
    if (activeObject) {
      trackDeleteElement();
      canvas?.current?.remove(activeObject);
    }

    if (
      activeObject?.type === CanvasDataTypes.EditableText ||
      activeObject?.type === CanvasDataTypes.UserText ||
      activeObject?.type === CanvasDataTypes.PhotoZoneTextbox
    ) {
      textAction(ActionType.DELETE_TEXT, parseTextAction(activeObject as FabricTextBox));
    }
    return activeObject;
  };

  const handleClearConfirmation = () => {
    if (canvas?.current) {
      clearPhotoTextZone(canvas.current, SAS_DYNAMIC_BUTTONS);
      setAllDrawersClosed(appDispatch);
      closeClearConfirmationDialog();
    }
  };

  const handleResetConfirmation = () => {
    const activeImage = canvas?.current?.getActiveObject() as fabric.Object;
    if (!activeImage) {
      return;
    }

    if (activeImage.data?.type === CanvasDataTypes.WamImage) {
      addAction('remove-handwriting-from-card-face', {
        handwriting: activeImage.data,
      });
    }

    deleteImage(initializedData?.project_id as string, activeImage?.name as string).then((response) => {
      if (response?.meta?.code >= 400) {
        setIsToasterOpen(appDispatch, {
          title: imageErrorTitle,
          children: resetErrorMessage,
          variant: ToastVariants.Error,
        });
        closeResetConfirmationDialog();
      } else {
        const deletedImage = deleteSelectedObject();
        if (deletedImage && deletedImage.data?.zoneName && canvas?.current) {
          const photoTextZone = getObjectByName(deletedImage.data.zoneName, canvas.current) as fabric.Group;
          if (photoTextZone) {
            photoTextZone.data.hasContent = false;
            photoTextZone.setOptions({ selectable: true });
            togglePhotoTextZoneButtons(true, canvas.current, photoTextZone, SAS_DYNAMIC_BUTTONS);
            canvas?.current?.renderAll();
          }
        }
        setAllDrawersClosed(appDispatch);
        closeResetConfirmationDialog();

        if (deletedImage?.data?.zoneName?.indexOf(CanvasDataTypes.PhotoTextZone) !== -1) {
          //Track Wam/Tam reset
          trackWamTamReset(false);
        }
      }
    });
  };

  const handleDeleteConfirmation = async () => {
    const activeObject = canvas?.current?.getActiveObject() as FabricObject | null;

    if (!canvas || !canvas.current || !activeObject) return;

    // TO DO: Handle the case where the active object is a text zone using a function similar to deleteSelectedPhoto
    // That way we can have a consistent way to handle deletion with hooks
    if (isFabricImage(activeObject)) {
      await deleteSelectedPhoto();
    } else {
      // This is currently used for deleting texts
      deleteSelectedObject();
    }

    // Restore editable area buttons if all objects are removed from editable area
    if (canvas?.current) {
      const objects = canvas.current.getObjects();
      if (isUserZoneEmpty(objects)) {
        // User zone is empty, so populate user zone buttons
        const cardFace = cardState.cardFacesList[`${cardState.activeCardIndex}`];
        const { editableAreas } = cardFace;
        if (editableAreas) {
          addEditableAreas(cardFace, cardState, true, isOneToMany);
        }
      }
    }
    setAllDrawersClosed(appDispatch);
    closeDeleteConfirmationDialog();
  };

  const handleSizeChange = (textObject: fabric.Textbox | null, valueInRange: string) => {
    if (textObject) {
      const scaledFontSize = convertPointToPixel(parseInt(valueInRange));
      textObject.setOptions({ fontSize: scaledFontSize });
      updateEditFormats({ fontSize: valueInRange });
      canvas?.current?.renderAll();
      textObject.fire('changed');
    }
  };

  return {
    handleRotateOpenDrawer,
    onChangeOrder,
    openDeleteConfirmationDialog,
    closeDeleteConfirmationDialog,
    closeClearConfirmationDialog,
    onSubmitSize,
    onSubmitRotate,
    onSubmitOrder,
    onFinishCropping,
    onCloseImageEditDrawer,
    onSizeDrawerClose,
    closeResetConfirmationDialog,
    handleClearConfirmation,
    deleteSelectedObject,
    handleResetConfirmation,
    handleDeleteConfirmation,
    handleSizeChange,
  };
};
